// Typed API client for the Go backend. Auth headers are injected by the auth
// layer via setAuthHeaderProvider (Bearer token in Entra mode, X-Dev-* headers
// in dev mode), so components never deal with tokens directly.
/** File-upload policy reported by the server (GET /api/user/context). */
export interface UploadInfo {
    enabled: boolean
    maxMB: number
    /** Accepted extensions, e.g. '.pdf'. */
    allowed: string[]
    maxPerField: number
    minOverrideLen: number
}

export interface UserContext {
    userId: string
    name: string
    email: string
    isRiskManager: boolean
    uploads?: UploadInfo
}

export interface UserRef {
    id: string
    name: string
    email: string
    is_risk_manager?: boolean
}

import { parseContentDispositionFilename } from '@/platform/lib/files'

const API_BASE = import.meta.env.VITE_API_BASE || ''

type HeaderProvider = () => Promise<Record<string, string>>
let authHeaderProvider: HeaderProvider = async () => ({})

/** Registered by the auth layer; supplies Authorization / X-Dev-* headers. */
export function setAuthHeaderProvider(fn: HeaderProvider) {
    authHeaderProvider = fn
}

export class ApiError extends Error {
    status: number
    /** Validation problems from a 422 response (form/workflow/dashboard saves). */
    problems: string[]
    /** Field keys reported as unanswered by a failed submit. */
    missing: string[]
    constructor(status: number, message: string, problems: string[] = [], missing: string[] = []) {
        super(message)
        this.status = status
        this.problems = problems
        this.missing = missing
    }
}

async function buildHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    const auth = await authHeaderProvider()
    return { Accept: 'application/json', ...auth, ...extra }
}

async function parseError(res: Response): Promise<never> {
    let msg = `HTTP ${res.status}`
    let problems: string[] = []
    let missing: string[] = []
    try {
        const data = await res.json()
        if (data?.error) msg = data.error
        // The override gate carries a human-readable explanation in `message`.
        if (data?.error === 'override_required' && data?.message) msg = data.message
        if (Array.isArray(data?.problems)) problems = data.problems
        if (Array.isArray(data?.missing)) missing = data.missing
    } catch {
        /* ignore */
    }
    throw new ApiError(res.status, msg, problems, missing)
}

export async function requestJSON<T>(
    method: string, path: string, body?: unknown, extraHeaders?: () => Promise<Record<string, string>>,
): Promise<T> {
    const headers = await buildHeaders({
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(extraHeaders ? await extraHeaders() : {}),
    })
    const res = await fetch(API_BASE + path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) return parseError(res)
    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
}

export async function requestBlob(path: string): Promise<Blob> {
    const headers = await buildHeaders()
    const res = await fetch(API_BASE + path, { headers })
    if (!res.ok) return parseError(res)
    return res.blob()
}

/** POST multipart form data (the browser sets the Content-Type boundary). */
export async function requestUpload<T>(path: string, body: FormData): Promise<T> {
    const headers = await buildHeaders()
    const res = await fetch(API_BASE + path, { method: 'POST', headers, body })
    if (!res.ok) return parseError(res)
    return res.json() as Promise<T>
}

/** GET a binary attachment; the filename comes from Content-Disposition. */
export async function requestDownload(path: string): Promise<{ blob: Blob; filename: string }> {
    const headers = await buildHeaders({ Accept: '*/*' })
    const res = await fetch(API_BASE + path, { headers })
    if (!res.ok) return parseError(res)
    const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'))
    return { blob: await res.blob(), filename }
}

export const api = {
    userContext: () => requestJSON<UserContext>('GET', '/api/user/context'),

    searchUsers: (q: string) =>
        requestJSON<{ items: UserRef[] }>('GET', `/api/users?q=${encodeURIComponent(q)}`).then((r) => r.items),

    getUser: (id: string) => requestJSON<UserRef>('GET', `/api/users/${encodeURIComponent(id)}`),

    // Cache a directory-picked user so the backend can resolve their email for
    // notifications even before they've ever signed in.
    cacheUser: (u: UserRef) =>
        requestJSON<UserRef>('POST', '/api/users', { id: u.id, name: u.name, email: u.email }),
}
